import * as React from "react";
import ScheduleTeamPickerPanel from "../scheduleteampicker/ScheduleTeamPickerPanel";
import StaffHubPanel from "sh-application/components/common/StaffHubPanel";
import StringsStore from "sh-strings/store";
import { ActionButton, Announced, IButton, IPanelProps, IRenderFunction, PanelType } from "@fluentui/react";
import { Menu, MenuButton, MenuItemRadio, MenuList, MenuPopover, MenuTrigger } from "@fluentui/react-components";
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from "owa-accessibility";
import { getGenericEventPropertiesObject } from "sh-instrumentation";
import { ECSConfigKey, ECSConfigService, InstrumentationService } from "sh-services";
import { ITeamInfoEntity, TeamCategoryType, TeamCategoryTypes } from "sh-models";
import { teamSelected } from "sh-team-store";
import { TeamSetupPickerStepProps } from "../TeamSetupPickerStepProps";
import { UrlFactory } from "sh-application";
import classNames from "classnames/bind";

const styles = require("./TeamPickerPanel.scss");
const panelWidth = "320px";

export interface TeamPickerPanelProps extends TeamSetupPickerStepProps {
    isOpen: boolean;
    disableAutofocus?: boolean;    // set to true to prevent the team picker panel from trying to set initial focus
    newScheduleBtnRef: React.RefObject<IButton>;
    selectedTeamCategoryType?: TeamCategoryType;
    onSelectTeamCategoryType?: (selectedTeamCategoryType: TeamCategoryType) => void;
    isTeamCategoryMenuOpen?: boolean;
    onTeamCategoryMenuOpenChange?: (open: boolean) => void;
    onKeyDown?: (e: React.KeyboardEvent<any>) => void;
}

/**
* Fabric panel component that allows a user to pick a team schedule to switch to, or start the schedule
* provisioning flow
*/
export default class TeamPickerPanel extends React.Component<TeamPickerPanelProps, {}> {
    private _strings: Map<string, string>;
    private _commonStrings: Map<string, string>;
    private _newScheduleBtnRef: React.RefObject<IButton>;

    constructor(props: TeamPickerPanelProps) {
        super(props);
        this._commonStrings = StringsStore().registeredStringModules.get("common").strings;
        this._strings = StringsStore().registeredStringModules.get("scheduleTeamPicker").strings;
        this._newScheduleBtnRef = props.newScheduleBtnRef;
    }

    /**
     *
     * @param checkedItems
     * @returns void
     * handles the team category type changed
    */
    private handleTeamCategoryTypeChanged = (_: any, { checkedItems }: { checkedItems: string[] }): void => {
        if (!checkedItems.length) {
            return;
        }

        const selectedTeamCategoryType = checkedItems[0] as TeamCategoryType;
        if (this.props.selectedTeamCategoryType === selectedTeamCategoryType) {
            return;
        }
        const eventMapping = {
            [TeamCategoryTypes.Active]: InstrumentationService.values.TeamCategoryDropdownActiveTeamsButtonSelected,
            [TeamCategoryTypes.Archived]: InstrumentationService.values.TeamCategoryDropdownArchivedTeamsButtonSelected,
            [TeamCategoryTypes.All]: InstrumentationService.values.TeamCategoryDropdownAllTeamsButtonSelected
        };
        const eventValue = eventMapping[selectedTeamCategoryType];
        if (eventValue) {
            InstrumentationService.logEvent(
                InstrumentationService.events.ScheduleTeamPicker,
                [getGenericEventPropertiesObject(InstrumentationService.properties.EventType, eventValue)]
            );
        }
        this.props.onSelectTeamCategoryType(selectedTeamCategoryType);
    };

    /**
     * creates the team category menu
     * @returns JSX.Element
    */
    private renderTeamCategoryMenu = (): JSX.Element => {
        const allTeamsCategoryLabel = this._strings.get("labelForAllTeamsCategory");
        const activeTeamsCategoryLabel = this._strings.get("labelForActiveTeamsCategory");
        const archivedTeamsCategoryLabel = this._strings.get("labelForArchivedTeamsCategory");

        const { selectedTeamCategoryType, isTeamCategoryMenuOpen, onTeamCategoryMenuOpenChange } = this.props;
        const teamCategoryPickerTextMap = {
            All: allTeamsCategoryLabel,
            Active: activeTeamsCategoryLabel,
            Archived: archivedTeamsCategoryLabel
        };

        return (
            <Menu checkedValues={{ teamCategory: [selectedTeamCategoryType] }} onCheckedValueChange={this.handleTeamCategoryTypeChanged}
                    open={isTeamCategoryMenuOpen}
                    onOpenChange={(_, data) => onTeamCategoryMenuOpenChange && onTeamCategoryMenuOpenChange(!!data.open)}
            >
                <MenuTrigger disableButtonEnhancement>
                    <MenuButton aria-label={`${this._strings.get("teamCategoryDropDownAriaLabel")}: ${teamCategoryPickerTextMap[selectedTeamCategoryType]}`}>
                        {teamCategoryPickerTextMap[selectedTeamCategoryType]}
                    </MenuButton>
                </MenuTrigger>
                <MenuPopover className={styles.teamCategoryMenuPopover}>
                    <MenuList>
                    {Object.entries(teamCategoryPickerTextMap).map(([key, label]) => (
                        <MenuItemRadio key={key} name="teamCategory" value={key} aria-label={label} >
                            {label}
                        </MenuItemRadio>
                    ))}
                    </MenuList>
                </MenuPopover>
            </Menu>
        );
    };
    /**
     * Handler for when the user selects a team to view
     */
    private handleTeamSelected = (team: ITeamInfoEntity) => {
        InstrumentationService.logEvent(
            InstrumentationService.events.ScheduleTeamPicker,
            [getGenericEventPropertiesObject(InstrumentationService.properties.EventType, InstrumentationService.values.ScheduleTeamSelectedClicked)]);

        // Send the user to the team page of the selected team
        // TODO DCoh: Also, hook up the navigationHandler once Teams exposes it in their SDK so that forward/back navigating within Teams works properly.
        teamSelected(team, UrlFactory.isSchedulesPageLocation(this.props.history.location.pathname) /*doInitializeSchedulesViewState*/);
        this.props.history.push(UrlFactory.getSchedulesUrl(team.id));
        this.props.onClose();
    }

    /**
     * Handler for when the user clicks on the Create New Schedule entrypoint
     */
    private handleCreateNewScheduleClicked = () => {
        InstrumentationService.logEvent(
            InstrumentationService.events.ScheduleTeamPicker,
            [getGenericEventPropertiesObject(InstrumentationService.properties.EventType, InstrumentationService.values.CreateNewScheduleButtonClicked)]);

        this.props.onGotoProvisionTeamFlow();
    }

    /**
     * Renders the custom panel header
     * @return {JSX.Element}
     */
    private onRenderPanelHeader = () => {
        // return an empty fragment to not render a custom header in the StaffHubPanel
        return (
            <></>
        );
    }

    /**
     * Renders the custom panel navigation component
     * @return {JSX.Element}
     */
    private onRenderNavigationContent = (props: IPanelProps, defaultRender: IRenderFunction<IPanelProps>): JSX.Element => {
        const ariaProps: AriaProperties = {
            role: AriaRoles.heading,
            label: this._strings.get("selectTeamPanelTitle")
        };

        const isSeparateArchiveTeamsViewEnabled = ECSConfigService.isECSFeatureEnabled(ECSConfigKey.SeparateArchiveTeamsViewEnabled);

        return (
            <>
                {isSeparateArchiveTeamsViewEnabled ? (
                    <div className={styles.navContainer}>
                        <Announced message={ this._strings.get("selectTeamPanelTitle")} />
                        <h2 { ...generateDomPropertiesForAria(ariaProps) } className={ classNames(
                            styles.navTitle, styles.navTitleWithPadding )}>
                            { this._strings.get("selectTeamPanelTitle") }
                        </h2>
                        <div className={styles.navTeamCategroryCommandBar} >
                            {this.renderTeamCategoryMenu()}
                        </div>
                    </div>
                ) : (
                    <div className={styles.navContainer}>
                        <h2 { ...generateDomPropertiesForAria(ariaProps) } className={ styles.navTitle }>{ this._strings.get("selectTeamPanelTitle") }</h2>
                    </div>
                )}
                { defaultRender(props) }
            </>
        );
    }

    /**
     * Renders the custom panel footer component
     * @return {JSX.Element}
     */
    private onRenderFooterContent = (): JSX.Element => {
        return (
            <ActionButton
                componentRef={ this._newScheduleBtnRef}
                className={ styles.createButton }
                onClick={ this.handleCreateNewScheduleClicked }
                iconProps={ { iconName: 'Add' } }
                allowDisabledFocus
                disabled={ false }
                checked={ false } >
                { this._strings.get("createNewScheduleButtonLabel") }
            </ActionButton>
        );
    }

    componentDidUpdate(prevProps: TeamPickerPanelProps) {
        // If menu just opened, add ESC listener
        if (!prevProps.isTeamCategoryMenuOpen && this.props.isTeamCategoryMenuOpen) {
            document.addEventListener('keydown', this.handleDocumentKeyDown, true);
        }
        // If menu just closed, remove ESC listener
        if (prevProps.isTeamCategoryMenuOpen && !this.props.isTeamCategoryMenuOpen) {
            document.removeEventListener('keydown', this.handleDocumentKeyDown, true);
        }
    }

    componentWillUnmount() {
        // Clean up in case
        document.removeEventListener('keydown', this.handleDocumentKeyDown, true);
    }

    private handleDocumentKeyDown = (e: KeyboardEvent) => {
        if (this.props.isTeamCategoryMenuOpen && (e.key === 'Escape' || e.key === 'Esc')) {
            e.preventDefault();
            e.stopPropagation();
            if (this.props.onTeamCategoryMenuOpenChange) {
                this.props.onTeamCategoryMenuOpenChange(false);
            }
        }
    };

    render() {
        const { isOpen, disableAutofocus, onClose, onCancel } = this.props;

        return (
            <div>
                <StaffHubPanel
                    contentClass={ styles.content }
                    childrenContainer={ styles.childrenContainer }
                    panelClass={ styles.teamPickerPanel }
                    onDismiss={ onClose }
                    onCancel={ onCancel }
                    hasCloseButton
                    cancelButtonLabel={this._commonStrings.get("closePanelAriaLabel")}
                    isOpen={ isOpen }
                    panelWidth={ panelWidth }
                    panelType={ PanelType.customNear }
                    isBlocking={ false }
                    hideFooter
                    isFooterAtBottom
                    onRenderNavigationContent={ this.onRenderNavigationContent }
                    onRenderFooterContent={ this.onRenderFooterContent }
                    onRenderHeader={ this.onRenderPanelHeader }
                >
                    <ScheduleTeamPickerPanel
                        instrumentationEventName={ InstrumentationService.events.ScheduleTeamPicker }
                        onTeamSelected={ this.handleTeamSelected }
                        onCreateNewScheduleClicked={ this.props.showCreateNewScheduleTeamEntrypoints ? this.handleCreateNewScheduleClicked : null }
                        autofocus={ !disableAutofocus }
                        onGotoProvisionTeamFlow={this.props.onGotoProvisionTeamFlow}
                />
                </StaffHubPanel>
            </div>
        );
    }

}